import { CompleteSampleReceptionFormData } from '../components/forms/CompleteSampleReceptionForm';

export type QcRoleValue = 'ECHANTILLON' | 'BIS' | 'BLANC' | 'MRC';
export type BlancSubtypeValue = 'PREPARATION' | 'ANALYTIQUE';

export interface BackendSampleData {
  external_id: string;
  client_id: string;
  nom_projet?: string;
  site_minier?: string;
  type_echantillon: string;
  priorite: string;
  
  // NEW: Custom lab reference for R-xxxxx-25-01 format
  custom_lab_reference?: string;
  
  // CRITICAL: These must be at top level for workflow routing
  analyses_demandees: string[];
  mechanical_preparation_methods?: string[];
  
  // 🔬 MULTI-LAB ROUTING: Custom element-to-lab mapping
  custom_lab_routing?: Record<string, string>;
  
  // 🆕 Element-to-method mapping for correct detection limits
  element_method_mapping?: Record<string, string>;

  // 🧪 Phase 1 — QA/QC role (per-sample)
  qc_role?: QcRoleValue;
  qc_blanc_subtype?: BlancSubtypeValue;
  parent_sample_id?: string;
  reference_material_id?: string;

  coordonnees: {
    latitude?: number;
    longitude?: number;
    elevation?: number;
    utm_x?: number;
    utm_y?: number;
    utm_zone?: string;
  };
  metadonnees: {
    poids_kg?: number;
    nombre_sacs?: number;
    etat_echantillon?: string;
    humidite_visible?: boolean;
    temperature_reception?: number;
    couleur?: string;
    texture?: string;
    description_physique?: string;
    echantillonnage_par?: string;
    methode_collecte?: string;
    collecteur?: string;
    date_collecte?: string;
    conditions_meteo?: string;
    equipement_utilise?: string;
    formation_geologique?: string;
    type_roche?: string;
    alteration?: string;
    mineralisation_visible?: string;
    profondeur_m?: number;
    reference_forage?: string;
    interval_de?: number;
    interval_a?: number;
    commentaires_geologue?: string;
    analyses_demandees: string[];
    selected_elements?: string[];
    mechanical_preparation_methods?: string[];
    priorite_analyse?: string;
    delai_souhaite?: number;
    instructions_speciales?: string;
    photos?: string[];
    certificat_transport?: string;
    commentaires_reception?: string;
    // CRITICAL: Batch information for proper batch handling
    batch_info?: {
      batch_number: string;
      sample_position: number;
      total_samples: number;
      is_batch_sample: boolean;
    };
  };
  commentaires?: string;
  date_prelevement?: string;
  batch_id?: string;
  localisation_stockage?: string;
  date_reception?: string;
}

export class SampleDataTransformer {
  /**
   * Transform frontend form data to backend API format
   */
  static transformToBackendFormat(
    formData: CompleteSampleReceptionFormData,
    options: {
      external_id?: string;
      sample_id?: string;
      batch_id?: string;
    } = {}
  ): BackendSampleData {
    // Generate external_id if not provided
    const external_id = options.external_id || 
      formData.external_id || 
      `${formData.site_minier || 'SITE'}-${formData.type_echantillon || 'SAMPLE'}-${Date.now().toString().slice(-6)}`;

    // Ensure required arrays are not empty
    const analyses_demandees = formData.analyses_demandees || [];
    const selected_elements = formData.selected_elements || [];
    
    // Validate that essential analysis data is present
    if (analyses_demandees.length === 0) {
      throw new Error('Au moins une analyse doit être sélectionnée');
    }

    // Transform coordinates
    const coordonnees = formData.coordonnees || {};
    
    // Make sure coordinates object has proper structure
    // Only set latitude/longitude if actually provided, otherwise use default values
    const transformedCoordonnees = {
      latitude: coordonnees.latitude || 31.6295, // Default to Morocco coordinates
      longitude: coordonnees.longitude || -7.9811, // Default to Morocco coordinates
      elevation: coordonnees.elevation,
      utm_x: coordonnees.utm_x,
      utm_y: coordonnees.utm_y,
      utm_zone: coordonnees.utm_zone
    };

    // Transform form data to backend format
    const backendData: BackendSampleData = {
      external_id,
      client_id: formData.client_id,
      nom_projet: formData.nom_projet || undefined,
      site_minier: formData.site_minier || undefined,
      type_echantillon: formData.type_echantillon,
      priorite: formData.priorite,
      coordonnees: transformedCoordonnees,
      
      // NEW: Custom lab reference for R-xxxxx-25-01 format
      custom_lab_reference: formData.custom_lab_reference,
      
      // CRITICAL: Include analyses_demandees at TOP LEVEL for workflow routing
      analyses_demandees: analyses_demandees,
      mechanical_preparation_methods: formData.mechanical_preparation_methods,
      
      // 🔬 MULTI-LAB ROUTING: Pass custom element-to-lab routing if provided
      custom_lab_routing: formData.custom_lab_routing,
      
      // 🆕 Element-to-method mapping for correct detection limits
      element_method_mapping: formData.element_method_mapping,

      // 🧪 Phase 1 — QA/QC role propagation (forwarded by createSampleData())
      qc_role: (formData as any).qc_role,
      qc_blanc_subtype: (formData as any).qc_blanc_subtype,
      parent_sample_id: (formData as any).parent_sample_id,
      reference_material_id: (formData as any).reference_material_id,

      metadonnees: {
        // Physical properties
        poids_kg: formData.poids_kg,
        nombre_sacs: formData.nombre_sacs,
        etat_echantillon: formData.etat_echantillon,
        humidite_visible: formData.humidite_visible,
        temperature_reception: formData.temperature_reception,
        couleur: formData.couleur,
        texture: formData.texture,
        description_physique: formData.description_physique,
        
        // Collection information
        echantillonnage_par: formData.echantillonnage_par,
        methode_collecte: formData.methode_collecte,
        collecteur: formData.collecteur,
        date_collecte: formData.date_collecte?.toISOString(),
        conditions_meteo: formData.conditions_meteo,
        equipement_utilise: formData.equipement_utilise,
        
        // Geological information
        formation_geologique: formData.formation_geologique,
        type_roche: formData.type_roche,
        alteration: formData.alteration,
        mineralisation_visible: formData.mineralisation_visible,
        profondeur_m: formData.profondeur_m,
        reference_forage: formData.reference_forage,
        interval_de: formData.interval_de,
        interval_a: formData.interval_a,
        commentaires_geologue: formData.commentaires_geologue,
        
        // Analysis requirements - ALSO in metadata for backward compatibility
        analyses_demandees: analyses_demandees,
        selected_elements: selected_elements,
        mechanical_preparation_methods: formData.mechanical_preparation_methods,
        priorite_analyse: formData.priorite_analyse,
        delai_souhaite: formData.delai_souhaite,
        instructions_speciales: formData.instructions_speciales,
        
        // Documentation
        photos: formData.photos || [],
        certificat_transport: formData.certificat_transport,
        commentaires_reception: formData.commentaires_reception,
        
        // CRITICAL: Batch information for proper batch handling
        batch_info: formData.batch_info || undefined,
        
        // NEW: Custom lab reference for R-xxxxx-25-01 format
        custom_lab_reference: formData.custom_lab_reference,
        
        // 🆕 CRITICAL: Element-to-method mapping for correct detection limits on certificates
        element_method_mapping: formData.element_method_mapping,
        
        // 🆕 CRITICAL: Custom lab routing for multi-lab samples
        custom_lab_routing: formData.custom_lab_routing,

        // Order number provided by client (displayed on certificate)
        order_number: (formData as any).order_number
      },
      commentaires: formData.commentaires_reception,
      date_prelevement: formData.date_collecte?.toISOString(),
      batch_id: options.batch_id,
      localisation_stockage: formData.localisation_stockage,
      date_reception: formData.date_reception?.toISOString()
    };

    return backendData;
  }

  /**
   * Transform backend sample data to frontend form format
   */
  static transformToFrontendFormat(backendData: any): Partial<CompleteSampleReceptionFormData> {
    const metadonnees = backendData.metadonnees || {};
    
    return {
      sample_id: backendData.sample_id,
      external_id: backendData.external_id,
      client_id: backendData.client_id,
      nom_projet: backendData.nom_projet,
      site_minier: backendData.site_minier,
      type_echantillon: backendData.type_echantillon,
      priorite: backendData.priorite,
      
      // Physical properties
      etat_echantillon: metadonnees.etat_echantillon || 'SOLIDE',
      humidite_echantillon: metadonnees.humidite_echantillon || 'SEC',
      humidite_visible: metadonnees.humidite_visible,
      poids_kg: metadonnees.poids_kg,
      nombre_sacs: metadonnees.nombre_sacs,
      temperature_reception: metadonnees.temperature_reception,
      couleur: metadonnees.couleur,
      texture: metadonnees.texture,
      description_physique: metadonnees.description_physique,
      
      // Collection information
      echantillonnage_par: metadonnees.echantillonnage_par || 'CLIENT',
      methode_collecte: metadonnees.methode_collecte,
      collecteur: metadonnees.collecteur,
      date_collecte: metadonnees.date_collecte ? new Date(metadonnees.date_collecte) : undefined,
      conditions_meteo: metadonnees.conditions_meteo,
      equipement_utilise: metadonnees.equipement_utilise,
      
      // GPS/Location
      coordonnees: backendData.coordonnees,
      
      // Geological information
      formation_geologique: metadonnees.formation_geologique,
      type_roche: metadonnees.type_roche,
      alteration: metadonnees.alteration,
      mineralisation_visible: metadonnees.mineralisation_visible,
      profondeur_m: metadonnees.profondeur_m,
      reference_forage: metadonnees.reference_forage,
      interval_de: metadonnees.interval_de,
      interval_a: metadonnees.interval_a,
      commentaires_geologue: metadonnees.commentaires_geologue,
      
      // Analysis requirements
      analyses_demandees: metadonnees.analyses_demandees || [],
      selected_elements: metadonnees.selected_elements || [],
      mechanical_preparation_methods: metadonnees.mechanical_preparation_methods || [],
      priorite_analyse: metadonnees.priorite_analyse || 'NORMALE',
      delai_souhaite: metadonnees.delai_souhaite,
      instructions_speciales: metadonnees.instructions_speciales,
      
      // Documentation
      photos: metadonnees.photos || [],
      certificat_transport: metadonnees.certificat_transport,
      commentaires_reception: metadonnees.commentaires_reception,
      
      // Timestamps
      date_reception: backendData.date_reception ? new Date(backendData.date_reception) : new Date()
    };
  }

  /**
   * Validate form data before submission
   */
  static validateFormData(formData: CompleteSampleReceptionFormData): string[] {
    const errors: string[] = [];

    // Required fields validation
    if (!formData.client_id) {
      errors.push('Client est requis');
    }

    if (!formData.type_echantillon) {
      errors.push('Type d\'échantillon est requis');
    }

    if (!formData.analyses_demandees || formData.analyses_demandees.length === 0) {
      errors.push('Au moins une analyse doit être sélectionnée');
    }

    // Batch validation
    if (formData.nombre_sacs && formData.nombre_sacs > 1) {
      if (formData.nombre_sacs > 100) {
        errors.push('Le nombre de sacs ne peut pas dépasser 100');
      }
    }

    // Weight validation
    if (formData.poids_kg && formData.poids_kg <= 0) {
      errors.push('Le poids doit être supérieur à 0');
    }

    // GPS validation
    if (formData.coordonnees) {
      const { latitude, longitude } = formData.coordonnees;
      if (latitude && (latitude < -90 || latitude > 90)) {
        errors.push('La latitude doit être entre -90 et 90');
      }
      if (longitude && (longitude < -180 || longitude > 180)) {
        errors.push('La longitude doit être entre -180 et 180');
      }
    }

    return errors;
  }

  /**
   * Generate sample external ID
   */
  static generateExternalId(formData: CompleteSampleReceptionFormData, sampleNumber?: number): string {
    const site = formData.site_minier || 'SITE';
    const type = formData.type_echantillon || 'SAMPLE';
    const year = new Date().getFullYear();
    const timestamp = Date.now().toString().slice(-6);
    
    if (sampleNumber) {
      return `${site}-${type}-${year}-${sampleNumber.toString().padStart(3, '0')}`;
    }
    
    return `${site}-${type}-${year}-${timestamp}`;
  }
}

export default SampleDataTransformer; 